import { Controller } from "@hotwired/stimulus";

const callbackName = "__buyoutGoogleMapsLoaded";
const loadTimeoutMilliseconds = 15_000;
let placesLibraryPromise;
let loadFailureReported = false;

function loadedPlacesLibrary () {
  const places = window.google?.maps?.places;

  return places?.Autocomplete ? places : undefined;
}

async function importPlacesLibrary () {
  const places = loadedPlacesLibrary() || await window.google.maps.importLibrary("places");

  if (!places?.Autocomplete) {
    throw new Error("Google Maps loaded without the Places Autocomplete library.");
  }

  return places;
}

function requestGoogleMaps (apiKey) {
  return new Promise((resolve, reject) => {
    const script = window.document.createElement("script");
    const query = new URLSearchParams({
      callback: callbackName,
      key: apiKey,
      loading: "async",
    });

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      delete window[callbackName];
      script.onerror = null;
    };
    const resolvePlaces = places => {
      cleanup();
      resolve(places);
    };
    const rejectLoad = error => {
      cleanup();
      reject(error);
    };
    const timeoutId = window.setTimeout(
      () => reportLoadFailure(new Error("Google Maps Places library is taking too long to load.")),
      loadTimeoutMilliseconds,
    );

    window[callbackName] = () => importPlacesLibrary().then(resolvePlaces, rejectLoad);
    script.async = true;
    script.onerror = () => rejectLoad(new Error("Google Maps JavaScript API failed to load."));
    script.src = `https://maps.googleapis.com/maps/api/js?${query}`;
    window.document.head.append(script);
  });
}

function reportLoadFailure (error) {
  if (loadFailureReported) return;

  loadFailureReported = true;
  window.setTimeout(() => { throw error; });
}

function loadPlacesLibrary (apiKey) {
  const places = loadedPlacesLibrary();

  if (places) return Promise.resolve(places);
  if (!apiKey) return Promise.resolve();

  if (placesLibraryPromise === undefined) {
    const loader = window.google?.maps?.importLibrary
      ? importPlacesLibrary()
      : requestGoogleMaps(apiKey);

    placesLibraryPromise = loader.catch(cause => {
      reportLoadFailure(new Error("Google Maps Places library failed to load.", { cause }));
      return undefined;
    });
  }

  return placesLibraryPromise;
}

export default class extends Controller {
  static targets = ["googleAddress", "streetAddress", "city", "state", "zipCode", "country", "lat", "lng", "googlePlaceId", "errorText", "nextSubmit"];
  static values = { googleMapsApiKey: String };

  async connect () {
    this.connected = true;
    const places = await loadPlacesLibrary(this.googleMapsApiKeyValue);

    if (!this.connected || !places || this.autocomplete) return;

    this.autocomplete = new places.Autocomplete(this.googleAddressTarget);
    this.autocomplete.setFields(["address_components", "geometry", "name", "formatted_address", "place_id"]);
    this.placeChangedListener = this.autocomplete.addListener("place_changed", this.validatePlace.bind(this));
  }

  disconnect () {
    this.connected = false;
    this.placeChangedListener?.remove();
    if (this.autocomplete) window.google?.maps?.event?.clearInstanceListeners(this.autocomplete);
    this.autocomplete = undefined;
  }

  validatePlace () {
    if (this.autocomplete === undefined) {
      return;
    }

    const place = this.autocomplete.getPlace();

    if (place) {
      this.validateAddress(place);

      if (place.address_components) {
        this.setAddressTargets(place.address_components);
      }

      if (place.geometry) {
        this.latTarget.value = place.geometry.location.lat();
        this.lngTarget.value = place.geometry.location.lng();
      }

      if (place.place_id) {
        this.googlePlaceIdTarget.value = place.place_id;
      }
      if (place.name && place.formatted_address) {
        // When searching by place name, the autofilled input box will not include the street number
        // place.name will either be street_number + route (i.e. 123 Main St) or a place name (i.e. Olive Garden)
        if (!place.formatted_address.includes(place.name)) {
          this.googleAddressTarget.value = `${place.name}, ${place.formatted_address}`;
        } else {
          this.googleAddressTarget.value = place.formatted_address;
        }
      }
    }
  }

  clearPlace () {
    this.clearAddressTargets();
    if (this.autocomplete !== undefined && this.autocomplete.getPlace()) {
      this.autocomplete.set("place", null);
    }
  }

  onBlur () {
    if (this.autocomplete === undefined) {
      return;
    }

    // Set small timeout because blur will occur before place_changed event causing the error to flash briefly
    setTimeout(() => this.validateAddress(this.autocomplete.getPlace()), 150);
  }

  placeChanged () {
    return this.googleAddressTarget.defaultValue !== this.googleAddressTarget.value;
  }

  clearAddressTargets () {
    this.streetAddressTarget.value = null;
    this.cityTarget.value = null;
    this.stateTarget.value = null;
    this.zipCodeTarget.value = null;
    this.countryTarget.value = null;

    this.latTarget.value = null;
    this.lngTarget.value = null;
    this.googlePlaceIdTarget.value = null;
  }

  validateAddress (place) {
    if (this.placeChanged()) {
      if (this.googleAddressTarget.value === "" || (place && place.address_components)) {
        this.removeError();
      } else {
        this.showError();
      }
    } else {
      this.removeError();
    }
    this.element.closest("form").dispatchEvent(new CustomEvent("google-address:change"));
  }

  showError () {
    this.googleAddressTarget.classList.add("error");
    this.errorTextTarget.classList.remove("hidden");
    if (this.hasNextSubmitTarget) this.nextSubmitTarget.disabled = true;
  }

  removeError () {
    this.googleAddressTarget.classList.remove("error");
    this.errorTextTarget.classList.add("hidden");
    if (this.hasNextSubmitTarget) this.nextSubmitTarget.disabled = false;
  }

  setAddressTargets (addressComponents) {
    let streetNumber = "";
    let route = "";

    addressComponents.forEach(component => {
      const name = component.long_name;
      const type = component.types[0];

      switch (type) {
        case "street_number":
          streetNumber = name;
          break;
        case "route":
          route = name;
          break;
        case "locality":
        case "sublocality_level_1": // specific to NYC
          this.cityTarget.value = name;
          break;
        case "administrative_area_level_1":
          this.stateTarget.value = name;
          break;
        case "postal_code":
          this.zipCodeTarget.value = name;
          break;
        case "country":
          this.countryTarget.value = name;
          break;
      }
    });

    this.streetAddressTarget.value = `${streetNumber} ${route}`;
  }

  keydown (event) {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }
}
